// Shared 1s rAF clock; auto-stops when the subscriber set empties.
// On tab-refocus the rAF fires from the current Date.now() — no missed-tick backlog.

type Tick = (nowMs: number) => void;

const TICK_INTERVAL_MS = 1000;

export class PlaybackClock {
  private subscribers = new Set<Tick>();
  private rafId: number | null = null;
  private lastEmit = 0;

  subscribe(cb: Tick): () => void {
    this.subscribers.add(cb);
    this.ensureRunning();
    return () => {
      this.subscribers.delete(cb);
      if (this.subscribers.size === 0) this.stop();
    };
  }

  private ensureRunning(): void {
    if (this.rafId == null && typeof requestAnimationFrame === 'function') {
      this.rafId = requestAnimationFrame(this.loop);
    }
  }

  private loop = (): void => {
    const now = Date.now();
    if (now - this.lastEmit >= TICK_INTERVAL_MS) {
      this.lastEmit = now;
      this.subscribers.forEach((cb) => {
        try { cb(now); }
        catch (err) { console.error('[PlaybackClock] subscriber threw, clock continues', err); }
      });
    }
    if (this.subscribers.size > 0 && typeof requestAnimationFrame === 'function') {
      this.rafId = requestAnimationFrame(this.loop);
    } else {
      this.rafId = null;
    }
  };

  private stop(): void {
    if (this.rafId != null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.rafId);
    }
    this.rafId = null;
  }
}

// Process-wide singleton — a stateless wall-clock tick shared by all consumers.
export const playbackClock = new PlaybackClock();
